/**
 * The pages the app can be sent to, by name: the agent speaks these ids and params, never
 * paths, and this module is the only thing that turns them into paths. It applies the same
 * checks `AppRoutes2` applies, so a page the user cannot open resolves to nothing, and it
 * reads a location back into a page for the context the chat sends.
 *
 * Kept free of React: the registry is data plus a few pure functions, so the published
 * catalog can be built from it anywhere (`agentCatalog.ts`).
 */
import { ROUTES } from '../constants/RouteNameMapping';
import { PermissionKey } from '../services/PermissionAndRolesKeys';
import { DealBoardDealCategory } from '../types';
import { MaggieFeatureFlags } from '../util/feature-flags';

/** The product areas the agent groups pages by. `atlas` and `argus` reach it only beside those tools. */
export const AGENT_AREAS = [
  'portfolio',
  'companies',
  'sourcing',
  'pipeline',
  'finance',
  'entities',
  'docs',
  'kpis',
  'atlas',
  'argus',
] as const;
export type Area = (typeof AGENT_AREAS)[number];

/** Param types naming an entity whose id the agent gets from a tool; the agent checks those ids. */
export const ENTITY_PARAM_TYPES = [
  'company',
  'fund',
  'view',
  'sourcingCompany',
  'sourcingSearch',
  'reconUpload',
] as const;
export type ParamType = (typeof ENTITY_PARAM_TYPES)[number] | 'string' | 'integer' | 'enum';

export interface ParamSpec {
  readonly type: ParamType;
  /** Default true. */
  readonly required?: boolean;
  /** `enum` only. */
  readonly values?: readonly string[];
}
export type ParamSpecs = Readonly<Record<string, ParamSpec>>;
export type ParamValue = string | number;
export type Params = Readonly<Record<string, ParamValue>>;

/** What the route table reads to decide which pages exist for this user. */
export interface AccessContext {
  hasPermission(key: PermissionKey): boolean;
  readonly isAuditor: boolean;
  readonly flags: Partial<MaggieFeatureFlags>;
  /** The `Allow_Fund_Waterfall` client config. */
  readonly fundWaterfallAllowed: boolean;
  readonly entityManagementEnabled: boolean;
  readonly beneficialOwnershipEnabled: boolean;
  /** Document Hub: its flag plus consolidated data collection. */
  readonly documentHubEnabled: boolean;
  /** The `Collection_Only` client config: the route table has data collection and company profiles alone. */
  readonly collectionOnly: boolean;
  /** A managed-services or respondent persona, whose route table has none of these pages. */
  readonly restrictedPersona: boolean;
}

export interface Destination {
  readonly title: string;
  /** One line, written for the agent. */
  readonly description: string;
  readonly area: Area;
  readonly params: ParamSpecs;
  /** The route patterns the page lives on: the usual one, then any the route table gives some users instead. */
  readonly routes: readonly string[];
  /** The path for already validated params; `label` names the entity for a slug, `access` picks this user's route. */
  build(params: Params, label?: string, access?: AccessContext): string;
  /** The params when `pathname` is this page, else null. */
  match(pathname: string): Params | null;
  /** The checks `AppRoutes2` applies to this page; absent, every user has it. */
  allowed?(access: AccessContext): boolean;
  /** Earlier ids of this page, still in old messages. Never published. */
  readonly aliases?: readonly string[];
  /** Usable in-app, not published to the agent. */
  readonly agent?: false;
}

export const DESTINATION_ID = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)*$/;
const NUMERIC_TYPES = new Set<ParamType>(['company', 'fund', 'view', 'reconUpload', 'integer']);
const WHOLE_NUMBER = /^\d{1,18}$/;
const URL_UNFRIENDLY = /[~:\\/?#@!$&'()*+,;=%><`]+/g;

/** The slug the company profile route carries before the id: punctuation becomes dashes. */
export function toURLFriendlyStringWithDashes(text: string): string {
  return text.replaceAll(URL_UNFRIENDLY, '-');
}

function encodeSegment(spec: ParamSpec, value: ParamValue, label: string | undefined): string {
  if (spec.type === 'company') {
    // The company profile page reads its id from the slug's trailing `-<id>`.
    return `${toURLFriendlyStringWithDashes(label ?? 'company')}-${value}`;
  }
  return encodeURIComponent(String(value));
}

function decodeSegment(spec: ParamSpec, segment: string): ParamValue | undefined {
  if (spec.type === 'company') {
    const id = /-(\d+)$/.exec(segment)?.[1];
    return id ? Number(id) : undefined;
  }
  if (NUMERIC_TYPES.has(spec.type)) {
    return WHOLE_NUMBER.test(segment) ? Number(segment) : undefined;
  }
  let text: string;
  try {
    text = decodeURIComponent(segment);
  } catch {
    return undefined;
  }
  if (spec.type === 'enum') {
    return spec.values?.includes(text) ? text : undefined;
  }
  return text || undefined;
}

/** `build` and `match` for a path template whose `:name` segments take the param of that name. */
function pattern(template: string, params: ParamSpecs) {
  const segments = template.split('/').filter(Boolean);
  segments
    .filter((segment) => segment.startsWith(':'))
    .forEach((segment) => {
      if (!(segment.slice(1) in params)) {
        throw new Error(`Destination path ${template} names a param ${segment} it does not declare`);
      }
    });
  return {
    build: (values: Params, label?: string): string =>
      '/' +
      segments
        .map((segment) => {
          if (!segment.startsWith(':')) {
            return segment;
          }
          const name = segment.slice(1);
          return encodeSegment(params[name], values[name], label);
        })
        .join('/'),
    match: (pathname: string): Params | null => {
      const parts = pathname.split('/').filter(Boolean);
      if (parts.length !== segments.length) {
        return null;
      }
      const values: Record<string, ParamValue> = {};
      for (let index = 0; index < segments.length; index++) {
        const segment = segments[index];
        if (!segment.startsWith(':')) {
          if (segment !== parts[index]) {
            return null;
          }
          continue;
        }
        const name = segment.slice(1);
        const value = decodeSegment(params[name], parts[index]);
        if (value === undefined) {
          return null;
        }
        values[name] = value;
      }
      return values;
    },
  };
}

interface PageSpec {
  title: string;
  description: string;
  area: Area;
  /** Segments starting with `:` take the param of that name. */
  path: string;
  /** A route the table gives some users in place of `path`, with the same params. */
  alternative?: { path: string; when: (access: AccessContext) => boolean };
  params?: ParamSpecs;
  /** Values for optional params a caller leaves out. */
  defaults?: Params;
  /** The page's own checks; every page is also closed to a restricted persona. */
  allowed?: (access: AccessContext) => boolean;
  /** In the collection-only route table too (company profiles and data collection). */
  collectionOnly?: boolean;
  aliases?: readonly string[];
  agent?: false;
}

function page({
  title,
  description,
  area,
  path,
  alternative,
  params = {},
  defaults,
  allowed,
  collectionOnly = false,
  aliases,
  agent,
}: PageSpec): Destination {
  const usual = pattern(path, params);
  const other = alternative && { ...pattern(alternative.path, params), when: alternative.when };
  return {
    title,
    description,
    area,
    params,
    routes: alternative ? [path, alternative.path] : [path],
    build: (values, label, access) => {
      const route = other && access && other.when(access) ? other : usual;
      return route.build(defaults ? { ...defaults, ...values } : values, label);
    },
    match: (pathname) => usual.match(pathname) ?? other?.match(pathname) ?? null,
    allowed: (access) =>
      !access.restrictedPersona && (collectionOnly || !access.collectionOnly) && (allowed?.(access) ?? true),
    aliases,
    agent,
  };
}

const companyId: ParamSpecs = { companyId: { type: 'company' } };
const fundId: ParamSpecs = { fundId: { type: 'fund' } };
const COMPANY = `/${ROUTES.COMPANY_PROFILES}/:companyId`;
const FUND = `/${ROUTES.FUND_PROFILES}/:fundId`;
const ADMIN_CONFIG = `/${ROUTES.ADMIN_PANEL}/${ROUTES.CONFIG}`;

const can = (key: PermissionKey) => (access: AccessContext) => access.hasPermission(key);
/** The route table's `getShowSeparateMprRoute`: the multi-period report on its own route, and no other view. */
const multiperiodOnly = (access: AccessContext) =>
  access.hasPermission(PermissionKey.canViewMultiperiod) &&
  !access.hasPermission(PermissionKey.canViewMetrics);
const companyTab = (key: PermissionKey) => (access: AccessContext) =>
  access.hasPermission(key) && !access.isAuditor;
const fundTab = (enabled: (access: AccessContext) => boolean) => (access: AccessContext) =>
  access.hasPermission(PermissionKey.hasFundProfilesModule) && enabled(access);
const entities = (access: AccessContext) => access.entityManagementEnabled;
const atlas = (access: AccessContext) =>
  access.hasPermission(PermissionKey.canViewAppAdminPanel) &&
  access.hasPermission(PermissionKey.customerSuccessAll);
const argus = (access: AccessContext) =>
  access.hasPermission(PermissionKey.canViewAppAdminPanel) && Boolean(access.flags.showGlSoiRecon);

/**
 * Every page the agent, or the app, can name. Ids are a public contract: old messages carry
 * them, so a renamed page keeps its old id in `aliases`.
 */
export const DESTINATIONS: Readonly<Record<string, Destination>> = {
  company: page({
    title: 'Company profile',
    description: "A portfolio company's profile, opening on its default tab.",
    area: 'companies',
    path: COMPANY,
    params: companyId,
    collectionOnly: true,
  }),
  'company.summary': page({
    title: 'Company summary',
    description:
      "A portfolio company's summary: description, investment metrics, KPI charts, rounds and commentary.",
    area: 'companies',
    path: `${COMPANY}/${ROUTES.COMPANY_PROFILE_SUMMARY}`,
    params: companyId,
    allowed: can(PermissionKey.canViewMetrics),
    collectionOnly: true,
  }),
  'company.performance': page({
    title: 'Company performance',
    description: "A portfolio company's KPI performance over time.",
    area: 'companies',
    path: `${COMPANY}/${ROUTES.COMPANY_PROFILE_PERFORMANCE}`,
    params: companyId,
    allowed: companyTab(PermissionKey.canViewKpis),
    collectionOnly: true,
  }),
  'company.people': page({
    title: 'Company people',
    description: "A portfolio company's people: founders, executives and board.",
    area: 'companies',
    path: `${COMPANY}/${ROUTES.COMPANY_PROFILE_PEOPLE}`,
    params: companyId,
    allowed: (access) => Boolean(access.flags.showPeopleReporting) && !access.isAuditor,
    collectionOnly: true,
  }),
  'company.financials': page({
    title: 'Company financials',
    description: "A portfolio company's financial statements and reported financial KPIs.",
    area: 'companies',
    path: `${COMPANY}/${ROUTES.COMPANY_PROFILE_FINANCIALS}`,
    params: companyId,
    allowed: can(PermissionKey.canViewCompanyFinancials),
    collectionOnly: true,
  }),
  'company.cap_table': page({
    title: 'Company cap table',
    description: "A portfolio company's capitalization table: shareholders, securities and ownership.",
    area: 'companies',
    path: `${COMPANY}/${ROUTES.COMPANY_PROFILE_CAP_TABLE}`,
    params: companyId,
    allowed: companyTab(PermissionKey.canViewCaptable),
    collectionOnly: true,
  }),
  'company.scenarios': page({
    title: 'Company scenarios',
    description: "A portfolio company's exit scenario models.",
    area: 'companies',
    path: `${COMPANY}/${ROUTES.COMPANY_PROFILE_SCENARIOS}`,
    params: companyId,
    allowed: companyTab(PermissionKey.canViewScenarios),
    collectionOnly: true,
  }),
  'company.deals': page({
    title: 'Company deals',
    description: "A portfolio company's deals and rounds in the pipeline.",
    area: 'companies',
    path: `${COMPANY}/${ROUTES.DEALS}`,
    params: companyId,
    allowed: can(PermissionKey.canViewMetrics),
    collectionOnly: true,
  }),
  fund: page({
    title: 'Fund profile',
    description: "A fund's profile, opening on its summary.",
    area: 'portfolio',
    path: FUND,
    params: fundId,
    allowed: can(PermissionKey.hasFundProfilesModule),
  }),
  'fund.summary': page({
    title: 'Fund summary',
    description: "A fund's summary: performance metrics, investments and distributions.",
    area: 'portfolio',
    path: `${FUND}/${ROUTES.FP_SUMMARY}`,
    params: fundId,
    allowed: can(PermissionKey.hasFundProfilesModule),
  }),
  'fund.waterfall': page({
    title: 'Fund waterfall',
    description: "A fund's distribution waterfall: LP/GP splits, hurdles and carry.",
    area: 'portfolio',
    path: `${FUND}/${ROUTES.FP_WATERFALL}`,
    params: fundId,
    allowed: fundTab((access) => access.fundWaterfallAllowed),
  }),
  'fund.reserves': page({
    title: 'Fund reserves',
    description: "A fund's reserves: capital held back for follow-on investments.",
    area: 'portfolio',
    path: `${FUND}/${ROUTES.FP_RESERVES}`,
    params: fundId,
    allowed: fundTab((access) => Boolean(access.flags.showFundReserves)),
  }),
  'fund.capital_allocation': page({
    title: 'Fund capital allocation',
    description: "A fund's capital allocation model: how committed capital is planned across investments.",
    area: 'portfolio',
    path: `${FUND}/${ROUTES.FP_CAPITAL_ALLOCATION}`,
    params: fundId,
    allowed: fundTab((access) => Boolean(access.flags.showFundCapitalAllocation)),
  }),
  portfolio: page({
    title: 'Portfolio',
    description: "The portfolio reporting section, opening on the user's default view.",
    area: 'portfolio',
    path: `/${ROUTES.PORTFOLIO}`,
    allowed: can(PermissionKey.canViewMetrics),
  }),
  'portfolio.view': page({
    title: 'Saved portfolio view',
    description: 'A saved portfolio view: a company, fund, security, people or multi-period grid.',
    area: 'portfolio',
    path: `/${ROUTES.PORTFOLIO}/:viewId`,
    alternative: { path: `/${ROUTES.MULTIPERIOD_REPORTING}/:viewId`, when: multiperiodOnly },
    params: { viewId: { type: 'view' } },
    allowed: (access) => access.hasPermission(PermissionKey.canViewMetrics) || multiperiodOnly(access),
  }),
  'companies.lists': page({
    title: 'Company lists',
    description: 'Company lists: tracked, watch and custom lists of companies.',
    area: 'companies',
    path: `/${ROUTES.COMPANIES}/${ROUTES.LISTS}`,
    allowed: can(PermissionKey.canViewList),
  }),
  sourcing: page({
    title: 'Sourcing',
    description: 'The sourcing section: company lists, saved searches and new searches.',
    area: 'sourcing',
    path: `/${ROUTES.SOURCING}`,
    allowed: (access) => Boolean(access.flags.showSourcing),
  }),
  'sourcing.search_results': page({
    title: 'Sourcing search results',
    description: 'The results of a sourcing search the agent ran.',
    area: 'sourcing',
    path: `/${ROUTES.SOURCING}/${ROUTES.SOURCING_RESULTS}/:searchId`,
    params: { searchId: { type: 'sourcingSearch' } },
    allowed: (access) => Boolean(access.flags.showSourcing),
  }),
  'sourcing.profile': page({
    title: 'Sourcing company profile',
    description: "A sourcing company's profile: overview, traffic, news, people and funding.",
    area: 'sourcing',
    path: `/${ROUTES.SOURCING}/${ROUTES.SOURCING_PROFILE}/:identifier`,
    params: { identifier: { type: 'sourcingCompany' } },
    allowed: (access) => Boolean(access.flags.showSourcing),
  }),
  'pipeline.board': page({
    title: 'Pipeline board',
    description: 'The deal pipeline board for one deal category; current deals when none is given.',
    area: 'pipeline',
    path: `/${ROUTES.DEAL_FLOW}/${ROUTES.DEALS}/:category`,
    params: {
      category: {
        type: 'enum',
        required: false,
        values: Object.values(DealBoardDealCategory).filter(
          (value) => value !== DealBoardDealCategory.UNKNOWN
        ),
      },
    },
    defaults: { category: DealBoardDealCategory.CURRENT },
    allowed: can(PermissionKey.canViewDeal),
  }),
  finance: page({
    title: 'Finance',
    description: 'The finance section, opening on all transactions.',
    area: 'finance',
    path: `/${ROUTES.FINANCE2}/${ROUTES.FINANCE2_OVERVIEW}`,
    allowed: can(PermissionKey.canViewTransaction),
  }),
  'entities.structure_chart': page({
    title: 'Entity structure chart',
    description: "The legal entity structure chart: the firm's entities and their relationships.",
    area: 'entities',
    path: `/${ROUTES.ENTITIES}/${ROUTES.ENTITIES_STRUCTURE_CHART}`,
    allowed: entities,
  }),
  'entities.registry': page({
    title: 'Legal entity registry',
    description: 'The legal entity registry: every entity with its details.',
    area: 'entities',
    path: `/${ROUTES.ENTITIES}/${ROUTES.ENTITIES_REGISTRY}`,
    allowed: entities,
  }),
  'entities.do_slate': page({
    title: 'Directors and officers slate',
    description: 'The directors and officers slate across legal entities.',
    area: 'entities',
    path: `/${ROUTES.ENTITIES}/${ROUTES.ENTITIES_DO_SLATE}`,
    allowed: entities,
  }),
  'entities.beneficial_ownership': page({
    title: 'Beneficial ownership',
    description: 'Beneficial ownership across legal entities.',
    area: 'entities',
    path: `/${ROUTES.ENTITIES}/${ROUTES.ENTITIES_BENEFICIAL_OWNERSHIP}`,
    allowed: (access) => access.beneficialOwnershipEnabled,
  }),
  'entities.review_queues': page({
    title: 'Entity review queues',
    description: 'Proposed changes to legal entities awaiting review.',
    area: 'entities',
    path: `/${ROUTES.ENTITIES}/${ROUTES.ENTITIES_REVIEW_QUEUES}`,
    allowed: entities,
  }),
  docs: page({
    title: 'Documents',
    description: 'The document hub: documents collected from portfolio companies.',
    area: 'docs',
    path: `/${ROUTES.DOCS}`,
    allowed: (access) => access.documentHubEnabled,
  }),
  kpis: page({
    title: 'Data collection',
    description: 'KPI data collection, opening on the companies collected from.',
    area: 'kpis',
    path: `/${ROUTES.KPI}/${ROUTES.COMPANIES}`,
    allowed: can(PermissionKey.canReadDataCollection),
    collectionOnly: true,
  }),
  'atlas.console': page({
    title: 'Atlas console',
    description: "The Atlas agent's console: its status, portfolio, SOI comparison, config and actions.",
    area: 'atlas',
    path: `${ADMIN_CONFIG}/${ROUTES.ADMIN_PANEL_ATLAS_CONSOLE}`,
    allowed: atlas,
  }),
  'argus.monitor': page({
    title: 'Reconciliation monitor',
    description: 'The reconciliation monitor: SOI uploads, their runs and the data review of each run.',
    area: 'argus',
    path: `${ADMIN_CONFIG}/${ROUTES.ADMIN_PANEL_RECONCILIATION_MONITOR}`,
    allowed: argus,
  }),
  'argus.profiles': page({
    title: 'Argus profiles',
    description: 'The Argus extraction profiles.',
    area: 'argus',
    path: `${ADMIN_CONFIG}/${ROUTES.ADMIN_PANEL_ARGUS_PROFILES}`,
    allowed: argus,
  }),
  'argus.profile': page({
    title: 'Argus profile',
    description: 'One Argus extraction profile, with its rules and runs.',
    area: 'argus',
    path: `${ADMIN_CONFIG}/${ROUTES.ADMIN_PANEL_ARGUS_PROFILES}/:profileId`,
    params: { profileId: { type: 'string' } },
    allowed: argus,
  }),
};

/**
 * Routes the agent cannot be sent to, each with the reason. Every `ROUTES` value is either
 * part of a destination's path or listed here, so a new page fails CI until someone decides
 * whether the agent should know about it.
 */
export const NOT_AGENT_DESTINATIONS: Readonly<Partial<Record<ROUTES, string>>> = {
  // Admin configuration: the admin panel keeps its own navigation in v1.
  [ROUTES.ADMIN_PANEL_AUDITOR_CONFIG]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_COLUMNS]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_DATA_COLLECTION]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_DATA_DISPLAY]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_FIELD_MANAGER]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_FIELDS]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_FORMATTERS]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_MANAGE_ACCESS]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_MANAGE_ACCESS_COMPANY]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_MANAGE_ACCESS_COLUMN_GRID_DISPLAY]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_MANAGE_ACCESS_FUND_ADMIN]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_MANAGE_ACCESS_AUDITOR_SETTINGS]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_MAPPINGS]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_ORG_PROFILE]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_ROUND_CREATION]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_ROUND_MANAGEMENT]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_ROUND_TRANSACTION_SOURCES]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_RUBRICS]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_TEARSHEET_GENERATION]: 'admin configuration',
  [ROUTES.ADMIN_PANEL_TABLE_DOWNLOAD]: 'admin configuration',
  [ROUTES.APPLICATION_SETTINGS]: 'admin configuration',
  [ROUTES.BULK_DEAL_DOCS]: 'admin configuration',
  [ROUTES.COMPANY_LIST]: 'admin configuration',
  [ROUTES.COMPANY_LIST_OVERVIEW]: 'admin configuration',
  [ROUTES.COMPANY_SUMMARY_KPI_CHARTS]: 'admin configuration',
  [ROUTES.COMPANY_SUMMARY_FINANCIAL_SETTINGS]: 'admin configuration',
  [ROUTES.COMPANY_SUMMARY_PERFORMANCE_SETTINGS]: 'admin configuration',
  [ROUTES.CONFIG_DEAL_TYPES]: 'admin configuration',
  [ROUTES.CONFIG_DEAL_STAGES]: 'admin configuration',
  [ROUTES.CONFIG_PASS_REASONS]: 'admin configuration',
  [ROUTES.CONFIG_MISS_REASONS]: 'admin configuration',
  [ROUTES.CONFIG_FIELD_AUTOMATION]: 'admin configuration',
  [ROUTES.CONFIG_FUNDS]: 'admin configuration',
  [ROUTES.CONFIG_SECTORS]: 'admin configuration',
  [ROUTES.CONFIG_KPI]: 'admin configuration',
  [ROUTES.CONFIG_KPI_GROUPS]: 'admin configuration',
  [ROUTES.CONFIG_KPI_SOURCES]: 'admin configuration',
  [ROUTES.DATA_DISPLAY_PORTFOLIO]: 'admin configuration',
  [ROUTES.DETAILS_CONFIG]: 'admin configuration',
  [ROUTES.GENERAL_LEDGER]: 'admin configuration',
  [ROUTES.KPI_DISPLAY]: 'admin configuration',
  [ROUTES.METRICS_CONFIG]: 'admin configuration',
  [ROUTES.PROCESSING]: 'admin configuration',
  [ROUTES.RESET_REINGESTION]: 'admin configuration',
  [ROUTES.SETTINGS]: 'admin configuration',
  [ROUTES.TEAR_SHEETS]: 'admin configuration',
  [ROUTES.TEAR_SHEETS_V1]: 'admin configuration',
  [ROUTES.TEAR_SHEETS_V2]: 'admin configuration',
  [ROUTES.TEAR_SHEETS_HEADER_CONFIG]: 'admin configuration',
  [ROUTES.TEAR_SHEETS_CAPITALIZATION_SUMMARY_CONFIG]: 'admin configuration',
  [ROUTES.TEAR_SHEETS_COMPANY_DESCRIPTION_CONFIG]: 'admin configuration',
  [ROUTES.TEAR_SHEETS_COMPANY_SUMMARY_CONFIG]: 'admin configuration',
  [ROUTES.TEAR_SHEETS_FINANCING_HISTORY_CONFIG]: 'admin configuration',
  [ROUTES.TEAR_SHEETS_INVESTMENT_SUMMARY_CONFIG]: 'admin configuration',
  [ROUTES.TEAR_SHEETS_INVESTMENT_THESIS_CONFIG]: 'admin configuration',
  [ROUTES.TEAR_SHEETS_PERFORMANCE_COMMENTARY_CONFIG]: 'admin configuration',
  [ROUTES.TEAR_SHEETS_PERFORMANCE_SUMMARY_CONFIG]: 'admin configuration',
  [ROUTES.UPDATE_DEAL_DOCS_ROUND]: 'admin configuration',
  [ROUTES.UPLOAD_STATUS]: 'admin configuration',
  [ROUTES.BULK_UPLOAD_REPORT]: 'the report of one bulk upload; its id comes from a toast, not a tool',
  // Integrations are out of v1.
  [ROUTES.INTEGRATIONS]: 'integrations',
  [ROUTES.INTEGRATIONS_APPS]: 'integrations',
  [ROUTES.INTEGRATIONS_APPS_AFFINITY]: 'integrations',
  [ROUTES.INTEGRATIONS_APPS_AIRTABLE]: 'integrations',
  [ROUTES.INTEGRATIONS_APPS_ALLVUE]: 'integrations',
  [ROUTES.INTEGRATIONS_APPS_ARKPES]: 'integrations',
  [ROUTES.INTEGRATIONS_APPS_CARTA]: 'integrations',
  [ROUTES.CARTA_FIRMS]: 'integrations',
  [ROUTES.CARTA_HOLDERS]: 'integrations',
  [ROUTES.CARTA_RUNS]: 'integrations',
  [ROUTES.INTEGRATIONS_APPS_ILEVEL]: 'integrations',
  [ROUTES.INTEGRATIONS_APPS_QUICKBOOKS]: 'integrations',
  [ROUTES.INTEGRATIONS_APPS_STANDARD_METRICS]: 'integrations',
  [ROUTES.INTEGRATIONS_APPS_COBALT]: 'integrations',
  [ROUTES.INTEGRATIONS_APPS_CONSOLIDATED_DATA_COLLECTION]: 'integrations',
  // KPI request and response flows are out of v1; `kpis` opens the section.
  [ROUTES.ASSIGNED_COMPANIES]: 'KPI request flows',
  [ROUTES.KPI_MANAGE]: 'KPI request flows',
  [ROUTES.KPI_REQUESTS]: 'KPI request flows',
  [ROUTES.KPI_REQUESTS_RESPONSES_HOME]: 'KPI request flows',
  [ROUTES.KPI_PENDING_REQUESTS_BASE]: 'KPI request flows',
  [ROUTES.KPI_PENDING_REQUESTS]: 'KPI request flows',
  [ROUTES.KPI_THANK_YOU_SCREEN]: 'KPI request flows',
  [ROUTES.KPI_REQUEST_REVIEW]: 'KPI request flows',
  [ROUTES.KPI_REQUESTS_IN_PROGRESS]: 'KPI request flows',
  [ROUTES.KPI_REQUESTS_ARCHIVED]: 'KPI request flows',
  [ROUTES.KPI_RESPONSES]: 'KPI request flows',
  [ROUTES.KPI_RESPONSES_ACCEPTED]: 'KPI request flows',
  [ROUTES.KPI_RESPONSES_MS_PENDING]: 'KPI request flows',
  [ROUTES.KPI_RESPONSES_MS_IN_PROGRESS]: 'KPI request flows',
  [ROUTES.KPI_RESPONSES_MS_REVIEWED]: 'KPI request flows',
  [ROUTES.KPI_RESPONSES_REJECTED]: 'KPI request flows',
  [ROUTES.KPI_RESPONSES_ANALYSIS]: 'KPI request flows',
  [ROUTES.KPI_CUSTOM_TEMPLATE_CONFIGURATION]: 'KPI request flows',
  [ROUTES.KPI_DATA_COLLECTION_CONFIGURATION]: 'KPI request flows',
  [ROUTES.KPI_TEMPLATES]: 'KPI request flows',
  [ROUTES.KPI_TEMPLATES_FULL_ROUTE]: 'KPI request flows',
  // Company profile tabs and sub-pages out of v1.
  [ROUTES.COMPANY_PROFILE_COMPETITORS]: 'company tab out of v1',
  [ROUTES.COMPANY_PROFILE_MAPPINGS]: 'company tab out of v1',
  [ROUTES.COMPANY_PROFILE_NICHE_VCS]: 'company tab out of v1',
  [ROUTES.COMPANY_PROFILE_SINGLE_SCENARIO]: 'one scenario; `company.scenarios` lists them',
  [ROUTES.COMPANY_PROFILE_SCENARIO_SETTINGS]: 'one scenario; `company.scenarios` lists them',
  [ROUTES.COMPANY_PROFILE_SCENARIO_RUN]: 'one scenario; `company.scenarios` lists them',
  [ROUTES.COMPANY_PROFILE_SCENARIO_WEIGHTED]: 'one scenario; `company.scenarios` lists them',
  [ROUTES.CAP_TABLE_FORM]: 'a form, not a page to read',
  // Finance sub-pages are out of v1; `finance` opens the section.
  [ROUTES.FINANCE]: 'legacy finance route',
  [ROUTES.FINANCE_ALL_SECURITIES]: 'finance sub-page',
  [ROUTES.FINANCE_RECON]: 'finance sub-page',
  [ROUTES.FINANCE_RECON_METRICS]: 'finance sub-page',
  [ROUTES.FINANCE_RECON_ROUNDS]: 'finance sub-page',
  [ROUTES.FINANCE_DETAILS]: 'finance sub-page',
  [ROUTES.FINANCE_TRANSACTION_HISTORY]: 'finance sub-page',
  [ROUTES.FINANCE_TRANSACTIONS_3]: 'finance sub-page',
  [ROUTES.FINANCE_ROUNDS]: 'finance sub-page',
  [ROUTES.FINANCE_LEDGER]: 'finance sub-page',
  [ROUTES.FINANCE_HISTORY]: 'legacy finance route',
  // Pipeline analytics are out of v1; `pipeline.board` opens the section.
  [ROUTES.DEAL_ANALYTICS]: 'pipeline analytics',
  [ROUTES.DEAL_ANALYTICS_KPI]: 'pipeline analytics',
  [ROUTES.DEAL_ANALYTICS_DURATION_DETAILS]: 'pipeline analytics',
  // Elsewhere.
  [ROUTES.SAVED_SEARCHES]: 'a saved sourcing search; the agent links the searches it runs',
  [ROUTES.COMPANIES_OVERVIEW]: 'the limited-profile landing page',
  [ROUTES.CHATBOT]: 'the assistant itself',
  [ROUTES.EXCEL_LOGIN]: 'the Excel add-in sign-in',
  [ROUTES.UI]: 'the component gallery, outside production',
};

export type DestinationRegistry = Readonly<Record<string, Destination>>;

const aliasesByRegistry = new WeakMap<DestinationRegistry, Readonly<Record<string, string>>>();

function aliasesOf(registry: DestinationRegistry): Readonly<Record<string, string>> {
  let aliases = aliasesByRegistry.get(registry);
  if (!aliases) {
    aliases = Object.fromEntries(
      Object.entries(registry).flatMap(([id, destination]) =>
        (destination.aliases ?? []).map((alias) => [alias, id] as const)
      )
    );
    aliasesByRegistry.set(registry, aliases);
  }
  return aliases;
}

/** The id a destination is registered under, following aliases; undefined when unknown. */
export function canonicalDestinationIdIn(registry: DestinationRegistry, id: string): string | undefined {
  if (Object.hasOwn(registry, id)) {
    return id;
  }
  return Object.hasOwn(aliasesOf(registry), id) ? aliasesOf(registry)[id] : undefined;
}

export function canonicalDestinationId(id: string): string | undefined {
  return canonicalDestinationIdIn(DESTINATIONS, id);
}

/** The destination's params from loosely typed input, or null when one is missing or wrong. */
export function coerceParams(specs: ParamSpecs, raw: Readonly<Record<string, unknown>>): Params | null {
  const params: Record<string, ParamValue> = {};
  for (const [name, spec] of Object.entries(specs)) {
    const value = raw[name];
    if (value === undefined || value === null || value === '') {
      if (spec.required === false) {
        continue;
      }
      return null;
    }
    const coerced = coerceParam(spec, value);
    if (coerced === undefined) {
      return null;
    }
    params[name] = coerced;
  }
  return params;
}

function coerceParam(spec: ParamSpec, value: unknown): ParamValue | undefined {
  if (NUMERIC_TYPES.has(spec.type)) {
    if (typeof value === 'number') {
      return Number.isInteger(value) && value >= 0 ? value : undefined;
    }
    return typeof value === 'string' && WHOLE_NUMBER.test(value) ? Number(value) : undefined;
  }
  const text = typeof value === 'string' ? value : typeof value === 'number' ? String(value) : undefined;
  if (text === undefined) {
    return undefined;
  }
  if (spec.type === 'enum') {
    return spec.values?.includes(text) ? text : undefined;
  }
  return text;
}

export interface ResolvedDestination {
  readonly id: string;
  readonly path: string;
  readonly params: Params;
}

export function resolveDestinationIn(
  registry: DestinationRegistry,
  id: string,
  rawParams: Readonly<Record<string, unknown>> | undefined,
  access: AccessContext,
  label?: string
): ResolvedDestination | null {
  const canonical = canonicalDestinationIdIn(registry, id);
  if (!canonical) {
    return null;
  }
  const destination = registry[canonical];
  const params = coerceParams(destination.params, rawParams ?? {});
  if (!params || (destination.allowed && !destination.allowed(access))) {
    return null;
  }
  return { id: canonical, path: destination.build(params, label, access), params };
}

/**
 * The path for a destination the user may open, or null: an unknown id (after aliases), a
 * missing or mistyped param, or a page the route table does not give this user.
 */
export function resolveDestination(
  id: string,
  rawParams: Readonly<Record<string, unknown>> | undefined,
  access: AccessContext,
  label?: string
): ResolvedDestination | null {
  return resolveDestinationIn(DESTINATIONS, id, rawParams, access, label);
}

/**
 * The path of a destination on its usual route, for the app's own links. Params are the caller's
 * to get right; a page some users reach on another route resolves through `resolveDestination`.
 */
export function buildPath(id: string, params: Params = {}, label?: string): string {
  if (!Object.hasOwn(DESTINATIONS, id)) {
    throw new Error(`Unknown destination ${id}`);
  }
  return DESTINATIONS[id].build(params, label);
}

export interface MatchedDestination {
  readonly id: string;
  readonly params: Params;
}

/** The destination a location is on, for the page context the chat sends; undefined when none. */
export function matchDestination(pathname: string): MatchedDestination | undefined {
  for (const [id, destination] of Object.entries(DESTINATIONS)) {
    const params = destination.match(pathname);
    if (params) {
      return { id, params };
    }
  }
  return undefined;
}
